import { Link } from '@tanstack/react-router'
import { ArrowRight, TriangleAlert } from 'lucide-react'

import { MoneyDisplay } from '@/components/money-display'
import { ErrorSurface } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatRatio, today } from '@/features/reports/constants'
import { usePortfolioAtRisk } from '@/features/reports/queries'
import { ReportHeaderNotice } from '@/features/reports/report-header-notice'
import { cn } from '@/lib/utils'

import { useOperatorBranchId } from './operator-branch'

/**
 * The branch's portfolio at risk as at today, read the same way the PAR screen's own summary
 * line reads it: `total`, not a sum of `lines`, because that is the figure the server stands
 * behind when a branch-reach reason has hidden a line from this reader.
 *
 * `asOf` is the browser's date here, exactly as `ParSection` defaults its own filter — a ceiling
 * for "as at today", never a business date standing in for a posting.
 *
 * The ratio is rendered through `formatRatio`, which reads null as a dash rather than 0.0% — an
 * empty portfolio and a portfolio in perfect health are not the same fact, and this tile never
 * conflates them.
 */
export function PortfolioAtRiskTile({ className }: { className?: string }) {
  const branchId = useOperatorBranchId()
  const asOf = today()
  const query = usePortfolioAtRisk({ asOf, branchId }, true)

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TriangleAlert className="size-4 text-muted" aria-hidden />
          Portfolio at risk
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-3">
        {query.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : query.isError ? (
          <ErrorSurface
            error={query.error}
            onRetry={() => void query.refetch()}
            title="Portfolio at risk could not be read"
          />
        ) : (
          <>
            <ReportHeaderNotice header={query.data.header} />

            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div className="min-w-0">
                <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">At risk</dt>
                <dd className="mt-0.5">
                  <MoneyDisplay
                    value={query.data.total.portfolioAtRisk.minor}
                    currency={query.data.total.portfolioAtRisk.currency}
                    emphasis="strong"
                    className="text-left"
                  />
                </dd>
              </div>

              <div className="min-w-0">
                <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">PAR ratio</dt>
                <dd className="numeric mt-0.5 text-sm font-medium tabular-nums text-heading">
                  {formatRatio(query.data.total.parRatio)}
                </dd>
              </div>

              <div className="min-w-0">
                <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">In arrears</dt>
                <dd className="mt-0.5">
                  <MoneyDisplay
                    value={query.data.total.amountInArrears.minor}
                    currency={query.data.total.amountInArrears.currency}
                    className="text-left"
                  />
                </dd>
              </div>

              <div className="min-w-0">
                <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">Facilities</dt>
                <dd className="numeric mt-0.5 text-sm font-medium tabular-nums text-heading">
                  {query.data.total.facilityCount}
                </dd>
              </div>
            </dl>
          </>
        )}

        <Button asChild variant="secondary" size="sm" className="self-start">
          <Link to="/reports/par">
            View portfolio at risk
            <ArrowRight />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
